import { Controller } from "@hotwired/stimulus"

// Manages the display of conditional fields based on campsite type
// Rules applied:
// - Hotwire/Stimulus: Dynamic UI interactions without full page reloads
// - Modularization: Encapsulated campsite filtering logic in a dedicated controller
export default class extends Controller {
  static targets = ["guestCountContainer", "padLengthContainer", "campsite"]
  static values = {
    defaultCampsiteType: String
  }

  initialize() {
    // Set the default campsite type
    this.defaultCampsiteType = this.defaultCampsiteTypeValue || 'all'
    
    // Initialize the current campsite type
    this.currentCampsiteType = this.defaultCampsiteType
    
    // Log the default campsite type
    console.log(`Initializing campsite filter with default type: ${this.defaultCampsiteType}`)
    
    // Set up Turbo Frame event listeners
    document.addEventListener("turbo:frame-load", this.handleTurboFrameLoad.bind(this))
  }

  connect() {
    console.log("CampsiteFilter controller connected")
    this.toggleFieldsBasedOnCampsiteType()
    
    // Listen for Turbo navigation events to update fields
    document.addEventListener('turbo:load', this.handleTurboEvent.bind(this))
    document.addEventListener('turbo:render', this.handleTurboEvent.bind(this))
    document.addEventListener('turbo:frame-render', this.handleTurboEvent.bind(this))
  }
  
  disconnect() {
    // Clean up event listeners
    document.removeEventListener('turbo:load', this.handleTurboEvent.bind(this))
    document.removeEventListener('turbo:render', this.handleTurboEvent.bind(this))
    document.removeEventListener('turbo:frame-render', this.handleTurboEvent.bind(this))
    document.removeEventListener("turbo:frame-load", this.handleTurboFrameLoad.bind(this))
  }
  
  handleTurboEvent() {
    this.toggleFieldsBasedOnCampsiteType()
  }

  handleTurboFrameLoad(event) {
    if (event.target.id === "campsite_type_selection") {
      console.log("Campsite type selection frame loaded")
      this.toggleFieldsBasedOnCampsiteType()
    }
  }

  toggleFieldsBasedOnCampsiteType(event) {
    const campsiteType = this.getCurrentCampsiteType()
    console.log("Toggling fields based on campsite type:", campsiteType)
    
    if (campsiteType === 'rv_camping') {
      this.showRvFields()
    } else {
      this.showNonRvFields()
    }
    
    // If this was triggered by a click event, preserve the date values
    if (event && event.type === 'click') {
      this.preserveDateValues()
    }
  }
  
  preserveDateValues() {
    // Find the date picker form on the page
    const form = document.querySelector('form.date-picker-container')
    if (!form) return
    
    // Get the date picker controller
    const datePickerController = this.application.getControllerForElementAndIdentifier(form, 'date-picker')
    if (!datePickerController) return
    
    console.log("Preserving date values during campsite type change")
    
    // Get the current date values from the form
    const startDateInput = form.querySelector('input[datepicker][id="datepicker-start"]')
    const endDateInput = form.querySelector('input[datepicker][id="datepicker-end"]')
    const hiddenStartDateInput = form.querySelector('input[name="start_date"][type="hidden"]')
    const hiddenEndDateInput = form.querySelector('input[name="end_date"][type="hidden"]')
    
    if (startDateInput && hiddenStartDateInput) {
      try {
        // Ensure the hidden input has the correct value
        const startDate = datePickerController.parseDate(startDateInput.value)
        if (startDate) {
          hiddenStartDateInput.value = datePickerController.formatDate(startDate)
          console.log("Preserved start date:", hiddenStartDateInput.value)
        }
      } catch (e) {
        console.error("Error preserving start date:", e)
      }
    }
    
    if (endDateInput && hiddenEndDateInput) {
      try {
        // Ensure the hidden input has the correct value
        const endDate = datePickerController.parseDate(endDateInput.value)
        if (endDate) {
          hiddenEndDateInput.value = datePickerController.formatDate(endDate)
          console.log("Preserved end date:", hiddenEndDateInput.value)
        }
      } catch (e) {
        console.error("Error preserving end date:", e)
      }
    }
    
    // Ensure the date values are synchronized
    if (typeof datePickerController.syncDatesBeforeSubmit === 'function') {
      datePickerController.syncDatesBeforeSubmit()
    }
  }
  
  getCurrentCampsiteType() {
    // First check URL parameters
    const urlParams = new URLSearchParams(window.location.search)
    let campsiteType = urlParams.get('campsite_type')
    
    // If not in URL, check form inputs
    if (!campsiteType) {
      const campsiteTypeInput = document.querySelector('input[name="campsite_type"]')
      if (campsiteTypeInput) {
        campsiteType = campsiteTypeInput.value
      }
    }
    
    // If still not found, use default value
    if (!campsiteType) {
      campsiteType = this.defaultCampsiteTypeValue || 'all'
    }
    
    return campsiteType
  }
  
  showRvFields() {
    if (this.hasPadLengthContainerTarget) {
      this.padLengthContainerTarget.style.display = ''
    }
    
    if (this.hasGuestCountContainerTarget) {
      this.guestCountContainerTarget.style.display = 'none'
    }
  }
  
  showNonRvFields() {
    if (this.hasPadLengthContainerTarget) {
      this.padLengthContainerTarget.style.display = 'none'
    }
    
    if (this.hasGuestCountContainerTarget) {
      this.guestCountContainerTarget.style.display = ''
    }
  }
}